import { useNavigate, useLocation } from "react-router-dom";
import { motion } from "framer-motion";
import { Home, Search, Plus, MessageCircle, User } from "lucide-react";
import { useQuery } from "@tanstack/react-query";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const haptic = () => { try { navigator.vibrate?.(8); } catch {} };

function Item({ icon: Icon, label, active, onClick, testid, badge }) {
  return (
    <button onClick={onClick} data-testid={testid}
            className="pressable relative flex flex-1 flex-col items-center gap-1 py-2">
      <div className="relative">
        <Icon size={23} color={active ? "var(--accent)" : "var(--text-dim)"}
              strokeWidth={active ? 2.6 : 2} />
        {badge > 0 && (
          <span className="absolute -right-2.5 -top-1.5 flex h-4 min-w-4 items-center justify-center rounded-full px-1 text-[10px] font-bold text-white"
                style={{ background: "var(--red)" }}>
            {badge > 9 ? "9+" : badge}
          </span>
        )}
      </div>
      <span className="text-[11px] font-semibold"
            style={{ color: active ? "var(--accent)" : "var(--text-dim)" }}>{label}</span>
    </button>
  );
}

export default function BottomNav() {
  const nav = useNavigate();
  const { pathname } = useLocation();
  const { user } = useAuth();

  const { data: unread } = useQuery({
    queryKey: ["unread"],
    queryFn: async () => (await api.get("/unread-count")).data.count,
    refetchInterval: 5000,
    enabled: !!user,
  });

  const go = (p) => { haptic(); nav(p); };
  const is = (p) => pathname === p || (p !== "/" && pathname.startsWith(p));

  return (
    <div className="fixed bottom-0 left-1/2 z-50 w-full -translate-x-1/2" style={{ maxWidth: 520 }}>
      <div className="glass mx-3 mb-3 flex items-end justify-around rounded-[26px] px-2 pb-1 pt-1"
           style={{ boxShadow: "var(--shadow)" }}>
        <Item icon={Home} label="Home" active={is("/")} onClick={() => go("/")} testid="nav-home" />
        <Item icon={Search} label="Browse" active={is("/browse")} onClick={() => go("/browse")} testid="nav-browse" />
        <div className="flex flex-1 justify-center">
          <motion.button
            whileTap={{ scale: 0.88 }}
            onClick={() => go("/post")}
            data-testid="nav-post"
            className="nav-fab -mt-6 flex h-16 w-16 flex-col items-center justify-center rounded-[22px] text-white"
            style={{ background: "linear-gradient(135deg,#F59E0B,#D97706)" }}
          >
            <Plus size={26} strokeWidth={3} />
            <span className="text-[10px] font-bold">Post</span>
          </motion.button>
        </div>
        <Item icon={MessageCircle} label="Messages" active={is("/messages")} onClick={() => go("/messages")} testid="nav-messages" badge={unread || 0} />
        <Item icon={User} label="Profile" active={is("/profile")} onClick={() => go("/profile")} testid="nav-profile" />
      </div>
    </div>
  );
}
